import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Calendar, FileText, Palette, Moon, Sparkles, LayoutGrid, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

const WHATS_NEW_VERSION = '2.0.0';
const STORAGE_KEY = 'whatsNewSeenVersion';

const WhatsNew: React.FC = () => {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const seenVersion = localStorage.getItem(STORAGE_KEY);
    if (seenVersion !== WHATS_NEW_VERSION) {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="h-6 w-6 text-primary" />
              {formatMessage({ id: 'WHATS_NEW_TITLE' })}
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={handleClose} className="h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          {/* Calendar */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{formatMessage({ id: 'WHATS_NEW_CALENDAR_TITLE' })}</h3>
              <p className="text-sm text-muted-foreground">
                {formatMessage({ id: 'WHATS_NEW_CALENDAR_DESC' })}
              </p>
              <ul className="text-sm text-muted-foreground mt-1 ml-4 list-disc space-y-0.5">
                <li>{formatMessage({ id: 'WHATS_NEW_CALENDAR_FEATURE_1' })}</li>
                <li>{formatMessage({ id: 'WHATS_NEW_CALENDAR_FEATURE_2' })}</li>
                <li>{formatMessage({ id: 'WHATS_NEW_CALENDAR_FEATURE_3' })}</li>
                <li>{formatMessage({ id: 'WHATS_NEW_CALENDAR_FEATURE_4' })}</li>
                <li>{formatMessage({ id: 'WHATS_NEW_CALENDAR_FEATURE_5' })}</li>
              </ul>
            </div>
          </div>

          {/* Blocks View */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <LayoutGrid className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{formatMessage({ id: 'WHATS_NEW_BLOCKS_TITLE' })}</h3>
              <p className="text-sm text-muted-foreground">
                {formatMessage({ id: 'WHATS_NEW_BLOCKS_DESC' })}
              </p>
            </div>
          </div>

          {/* PDF Export */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{formatMessage({ id: 'WHATS_NEW_PDF_TITLE' })}</h3>
              <p className="text-sm text-muted-foreground">
                {formatMessage({ id: 'WHATS_NEW_PDF_DESC' })}
              </p>
            </div>
          </div>

          {/* Updated UI */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Palette className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{formatMessage({ id: 'WHATS_NEW_UI_TITLE' })}</h3>
              <p className="text-sm text-muted-foreground">
                {formatMessage({ id: 'WHATS_NEW_UI_DESC' })}
              </p>
            </div>
          </div>

          {/* Dark Mode */}
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Moon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{formatMessage({ id: 'WHATS_NEW_DARKMODE_TITLE' })}</h3>
              <p className="text-sm text-muted-foreground">
                {formatMessage({ id: 'WHATS_NEW_DARKMODE_DESC' })}
              </p>
            </div>
          </div>

          {/* Feedback */}
          <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
            {formatMessage({ id: 'WHATS_NEW_FEEDBACK' })}{' '}
            <a href="mailto:support@timetrack.io" className="text-primary hover:underline">
              support@timetrack.io
            </a>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default WhatsNew;
